import { getEventPath, isValidDate, Platform } from '@tyler-components-web/core';
import { CalendarView, CALENDAR_CONSTANTS, RangeSelectionState, CalendarDateSelectType } from './calendar-constants';
import { findNextEnabledDay, findPreviousEnabledDay, getActiveDateIndex, getActiveDay, getDateCoordsFromIndex, getFirstActiveDayIndex, getLastActiveDayIndex, getNextDayIndex, getPreviousDayIndex } from './calendar-utils';
import { DateRange } from './core/date-range';
var CalendarFoundation = /** @class */ (function () {
    function CalendarFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._viewIndex = CalendarView.Date;
        this._month = new Date().getMonth();
        this._year = new Date().getFullYear();
        this._selectedDates = [];
        this._showToday = true;
        this._multiselect = false;
        this._minYear = new Date().getFullYear() - CALENDAR_CONSTANTS.numbers.DEFAULT_YEAR_RANGE;
        this._maxYear = new Date().getFullYear() + CALENDAR_CONSTANTS.numbers.DEFAULT_YEAR_RANGE;
        this._lastSelectedYear = this._year;
        this._preventFocus = false;
        this._autoSwitch = false;
        this._minDate = null;
        this._maxDate = null;
        this._blockedDates = [];
        this._dates = [];
        this._activeDateIndex = -1;
        this._isInitialized = false;
        this._mode = 'static';
        this._selectedLimit = Infinity;
        this._rangeSelectionState = RangeSelectionState.none;
        this._selectedRange = new DateRange();
        this._events = null;
        this._showHeader = true;
        this._monthButtonListener = function () { return _this._onMonthViewEntered(); };
        this._yearButtonListener = function () { return _this._onYearViewEntered(); };
        this._previousButtonListener = function () { return _this._onPreviousButtonClick(); };
        this._nextButtonListener = function () { return _this._onNextButtonClick(); };
        this._preventFocusListener = function (evt) { return evt.preventDefault(); };
        this._calendarHoverListener = function (evt) { return _this._onCalendarHover(evt); };
        this._calendarMouseoutListener = function (evt) { return _this._onLeave(evt); };
    }
    CalendarFoundation.prototype.initialize = function () {
        this._adapter.registerSelectMonthListener(this._monthButtonListener);
        this._adapter.registerSelectYearListener(this._yearButtonListener);
        this._adapter.registerPreviousMonthListener(this._previousButtonListener);
        this._adapter.registerNextMonthListener(this._nextButtonListener);
        if (this._range) {
            this._registerRangeListeners();
        }
        if (this._autoSwitch) {
            this._keepDateInView();
        }
        if (this._showHeader) {
            this._adapter.toggleHeaderVisibility(this._showHeader);
            this._adapter.setMonth(this._month);
            this._adapter.setYear(this._year);
            this._checkBoundaries();
            this._buildYears();
        }
        // Dates are built in _applyMode()
        this._applyMode();
        if (this._viewIndex !== CalendarView.Date) {
            this._applyViewIndex();
        }
        this._isInitialized = true;
    };
    CalendarFoundation.prototype.disconnect = function () {
        this._isInitialized = false;
        if (this._range) {
            this._unregisterRangeListeners();
        }
    };
    /** Add a new date to selectedDates */
    CalendarFoundation.prototype.selectDate = function (date) {
        var _this = this;
        if (this._selectedLimit === this._selectedDates.length) {
            return;
        }
        var formattedDate = new Date(date.setHours(0, 0, 0, 0));
        var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, formattedDate); });
        if (index === -1 && !this._isDisabled(formattedDate)) {
            if (this._multiselect) {
                this._selectedDates.push(formattedDate);
            }
            else {
                this._selectedDates = [formattedDate];
            }
            this._buildDates();
        }
    };
    /** Remove a date from selectedDates */
    CalendarFoundation.prototype.removeDate = function (date) {
        var _this = this;
        var formattedDate = new Date(date.setHours(0, 0, 0, 0));
        var index = this._selectedDates.findIndex(function (d) {
            return _this._isSameDate(d, formattedDate);
        });
        if (index !== -1) {
            if (this._multiselect) {
                this._selectedDates.splice(index, 1);
            }
            else {
                this._selectedDates = [];
            }
            this._buildDates();
        }
    };
    /** Add or remove a date in selectedDates */
    CalendarFoundation.prototype.toggleDate = function (date) {
        var _this = this;
        var formattedDate = new Date(date.setHours(0, 0, 0, 0));
        if (!this._isDisabled(date)) {
            var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, formattedDate); });
            if (!this._multiselect) {
                this._selectedDates = [];
            }
            if (index > -1) {
                if (this._multiselect) {
                    this._selectedDates.splice(index, 1);
                }
            }
            else {
                this._selectedDates.push(formattedDate);
            }
            this._buildDates();
        }
    };
    /** Updates the layout for any elements that are dependent on calculations based on container size/transformations. */
    CalendarFoundation.prototype.layout = function () {
        this._adapter.layout();
    };
    /** Handles key events from an external element. */
    CalendarFoundation.prototype.handleKey = function (key, isShiftKey) {
        if (isShiftKey === void 0) { isShiftKey = false; }
        var isNavKey = ['Home', 'End', 'Left', 'ArrowLeft', 'Right', 'ArrowRight', 'Up', 'ArrowUp', 'Down', 'ArrowDown'].includes(key);
        if (isNavKey && !isShiftKey) {
            switch (this._viewIndex) {
                case CalendarView.Date:
                    this._handleNavDayView(key);
                    break;
                case CalendarView.Month:
                    this._handleNavMonthView(key);
                    break;
                case CalendarView.Year:
                    this._handleNavYearView(key);
                    break;
            }
        }
        else if (key === 'Enter') {
            switch (this._viewIndex) {
                case CalendarView.Date:
                    var activeDay = getActiveDay(this._dates, this._activeDateIndex);
                    if (activeDay) {
                        this._onDateSelected(activeDay.date);
                    }
                    break;
                case CalendarView.Month:
                    var activeMonth = this._adapter.getActiveMonth();
                    if (activeMonth) {
                        this._onMonthSelected(activeMonth);
                    }
                    break;
                case CalendarView.Year:
                    var activeYear = this._adapter.getActiveYear();
                    if (typeof activeYear === 'number' && activeYear > 0) {
                        this._onYearSelected(activeYear);
                    }
                    break;
            }
        }
        else if (key === 'KeyM' && isShiftKey && this._viewIndex !== CalendarView.Month) {
            this._onMonthViewEntered();
        }
        else if (key === 'KeyY' && isShiftKey && this._viewIndex !== CalendarView.Year) {
            this._onYearViewEntered();
        }
        else if (key === 'KeyD' && isShiftKey && this._viewIndex !== CalendarView.Date) {
            this._viewIndex = CalendarView.Date;
            this._setActiveView();
            this._emitDateActiveChangeEvent();
        }
        else if (this._viewIndex === CalendarView.Date) {
            if (key === 'ArrowLeft' && isShiftKey) {
                this._tryNavigatePreviousMonth(this._activeDateIndex);
            }
            else if (key === 'ArrowRight' && isShiftKey) {
                this._tryNavigateNextMonth(this._activeDateIndex);
            }
        }
    };
    CalendarFoundation.prototype._handleNavDayView = function (key) {
        if (this._activeDateIndex === -1) {
            return this._initializeActiveDay();
        }
        var currentActiveDayIndex = this._activeDateIndex;
        var nextActiveDateIndex = currentActiveDayIndex;
        switch (key) {
            case 'Home':
                nextActiveDateIndex = getFirstActiveDayIndex(this._dates);
                break;
            case 'End':
                nextActiveDateIndex = getLastActiveDayIndex(this._dates);
                break;
            case 'Up':
            case 'ArrowUp':
                nextActiveDateIndex = getPreviousDayIndex('vertical', this._activeDateIndex);
                break;
            case 'Down':
            case 'ArrowDown':
                nextActiveDateIndex = getNextDayIndex('vertical', this._activeDateIndex);
                break;
            case 'Left':
            case 'ArrowLeft':
                nextActiveDateIndex = getPreviousDayIndex('horizontal', this._activeDateIndex);
                break;
            case 'Right':
            case 'ArrowRight':
                nextActiveDateIndex = getNextDayIndex('horizontal', this._activeDateIndex);
                break;
        }
        // Handle indexes that are outside the bounds
        if (nextActiveDateIndex < 0) {
            nextActiveDateIndex = this._tryNavigatePreviousMonth(currentActiveDayIndex);
        }
        else if (nextActiveDateIndex >= this._dates.length) {
            nextActiveDateIndex = this._tryNavigateNextMonth(currentActiveDayIndex);
        }
        else {
            var requestedDay = this._dates[nextActiveDateIndex];
            if (requestedDay && requestedDay.isThisMonth && requestedDay.disabled) {
                if (['ArrowLeft', 'ArrowUp'].includes(key)) {
                    // Find previous available non-disabled day in the current month starting at this location
                    nextActiveDateIndex = findPreviousEnabledDay(this._dates, currentActiveDayIndex - 1);
                    if (nextActiveDateIndex === -1) {
                        nextActiveDateIndex = this._tryNavigatePreviousMonth(currentActiveDayIndex);
                    }
                }
                else if (['ArrowRight', 'ArrowDown'].includes(key)) {
                    // Find next available non-disabled day in the current month starting at this location
                    nextActiveDateIndex = findNextEnabledDay(this._dates, currentActiveDayIndex + 1);
                    if (nextActiveDateIndex === -1) {
                        nextActiveDateIndex = this._tryNavigateNextMonth(currentActiveDayIndex);
                    }
                }
            }
        }
        // Handle days that are not part of the current month
        var activeDay = getActiveDay(this._dates, nextActiveDateIndex);
        if (activeDay && !activeDay.isThisMonth) {
            if (['ArrowLeft', 'ArrowUp'].includes(key)) {
                nextActiveDateIndex = this._tryNavigatePreviousMonth(currentActiveDayIndex);
            }
            else if (['ArrowRight', 'ArrowDown'].includes(key)) {
                nextActiveDateIndex = this._tryNavigateNextMonth(currentActiveDayIndex);
            }
        }
        // If the current date is a valid index then update the UI and emit an active change event
        if (currentActiveDayIndex !== nextActiveDateIndex && nextActiveDateIndex >= 0 && this._dates[nextActiveDateIndex]) {
            this._activeDateIndex = nextActiveDateIndex;
            this._emitDateActiveChangeEvent();
        }
    };
    CalendarFoundation.prototype._handleNavMonthView = function (key) {
        var activeMonthIndex = this._adapter.getActiveMonth();
        if (activeMonthIndex === null) {
            activeMonthIndex = 0;
        }
        switch (key) {
            case 'ArrowLeft':
                activeMonthIndex--;
                if (activeMonthIndex < 0) {
                    activeMonthIndex = 11;
                }
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
            case 'ArrowRight':
                activeMonthIndex++;
                if (activeMonthIndex > 11) {
                    activeMonthIndex = 0;
                }
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
            case 'ArrowUp':
                activeMonthIndex -= 3; // Number of columns in grid
                if (activeMonthIndex < 0) {
                    return;
                }
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
            case 'ArrowDown':
                activeMonthIndex += 3; // Number of columns in grid
                if (activeMonthIndex > 11) {
                    return;
                }
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
            case 'Home':
                activeMonthIndex = 0;
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
            case 'End':
                activeMonthIndex = 11;
                this._adapter.setActiveMonthIndex(activeMonthIndex);
                this._emitYearMonthChangeEvent(activeMonthIndex);
                break;
        }
    };
    CalendarFoundation.prototype._handleNavYearView = function (key) {
        var yearCount = this._adapter.getYearCount();
        var activeYearIndex = this._adapter.getActiveYearIndex();
        switch (key) {
            case 'ArrowUp':
                activeYearIndex--;
                if (activeYearIndex < 0) {
                    activeYearIndex = yearCount - 1;
                }
                this._adapter.setActiveYearIndex(activeYearIndex);
                this._emitYearActiveChangeEvent();
                break;
            case 'ArrowDown':
                activeYearIndex++;
                if (activeYearIndex >= yearCount) {
                    activeYearIndex = 0;
                }
                this._adapter.setActiveYearIndex(activeYearIndex);
                this._emitYearActiveChangeEvent();
                break;
            case 'Home':
                this._adapter.setActiveYearIndex(0);
                this._emitYearActiveChangeEvent();
                break;
            case 'End':
                this._adapter.setActiveYearIndex(yearCount - 1);
                this._emitYearActiveChangeEvent();
                break;
        }
    };
    CalendarFoundation.prototype.setActiveDate = function (date) {
        var _this = this;
        var dateIndex = this._dates.findIndex(function (d) { return _this._isSameDate(d.date, date); });
        if (dateIndex >= 0) {
            this._activeDateIndex = dateIndex;
            var coords = getDateCoordsFromIndex(dateIndex);
            this._adapter.setActiveDay(coords);
        }
    };
    CalendarFoundation.prototype._tryNavigatePreviousMonth = function (currentIndex) {
        if (!this._isMinMonth()) {
            this._goToPreviousMonth();
            return getLastActiveDayIndex(this._dates);
        }
        return currentIndex;
    };
    CalendarFoundation.prototype._tryNavigateNextMonth = function (currentIndex) {
        if (!this._isMaxMonth()) {
            this._goToNextMonth();
            return getFirstActiveDayIndex(this._dates);
        }
        return currentIndex;
    };
    CalendarFoundation.prototype._emitDateActiveChangeEvent = function () {
        if (this._activeDateIndex === -1) {
            return;
        }
        var coords = getDateCoordsFromIndex(this._activeDateIndex);
        this._adapter.setActiveDay(coords);
        var date = this._dates[this._activeDateIndex];
        var data = {
            type: 'date',
            text: date.date.toLocaleDateString(undefined, { month: 'long', day: '2-digit', year: 'numeric' }),
            selected: date.isSelected,
            rowIndex: coords[0],
            colIndex: coords[1],
            dayNumber: date.date.getDate()
        };
        this._emitActiveChangeEvent(data);
    };
    CalendarFoundation.prototype._emitYearActiveChangeEvent = function () {
        var year = this._adapter.getActiveYear();
        if (!year) {
            return;
        }
        var data = {
            type: 'year',
            text: "" + year,
            selected: this._year === year,
            year: year
        };
        this._emitActiveChangeEvent(data);
    };
    CalendarFoundation.prototype._emitYearMonthChangeEvent = function (month) {
        var data = {
            type: 'month',
            text: new Date(2000, month, 2).toLocaleString(Platform.TRIDENT ? 'en-US' : 'default', { month: 'long' }),
            selected: this._month === month,
            month: month
        };
        this._emitActiveChangeEvent(data);
    };
    CalendarFoundation.prototype._emitActiveChangeEvent = function (data) {
        if (!data) {
            return;
        }
        this._adapter.emitHostEvent(CALENDAR_CONSTANTS.events.ACTIVE_CHANGE, data, true, true);
    };
    /** Sets the active day based on the month currently in view. */
    CalendarFoundation.prototype._initializeActiveDay = function () {
        this._activeDateIndex = getActiveDateIndex(this._dates, this._activeDateIndex, this._selectedDates.length);
        if (this._activeDateIndex >= 0 && this._activeDateIndex < this._dates.length) {
            var activeDay = getActiveDay(this._dates, this._activeDateIndex);
            if (activeDay && (!activeDay.isThisMonth || activeDay.disabled)) {
                this._activeDateIndex = getFirstActiveDayIndex(this._dates);
            }
        }
        else {
            this._activeDateIndex = getFirstActiveDayIndex(this._dates);
        }
        this._adapter.setActiveDay(getDateCoordsFromIndex(this._activeDateIndex));
    };
    /** Construct an array of ICalendarDates to pass to the adapter. */
    CalendarFoundation.prototype._buildDates = function () {
        var dates = [];
        var startDate = new Date(this._year, this._month, 1);
        startDate.setDate(startDate.getDate() - this._firstDayOfMonth());
        var endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 42);
        for (var d = startDate; d < endDate; d.setDate(d.getDate() + 1)) {
            var isThisMonth = d.getMonth() === this._month && d.getFullYear() === this._year;
            var date = new Date(d);
            if (isThisMonth) {
                dates.push({
                    date: date,
                    isSelected: this._isSelected(d),
                    isThisMonth: isThisMonth,
                    isToday: this._showToday && this._isToday(d),
                    disabled: this._isDisabled(d),
                    events: this._getEventsForDate(date),
                    onSelect: this._beforeDateSelected.bind(this)
                });
            }
            else {
                dates.push({
                    date: date,
                    isSelected: false,
                    isThisMonth: isThisMonth,
                    isToday: false,
                    disabled: false,
                    events: this._getEventsForDate(date)
                });
            }
        }
        this._dates = dates;
        this._adapter.setDates(dates, this._selectedDates, this._mode === 'display', this._renderDateCallback);
        if (this._preventFocus) {
            this._initializeActiveDay();
        }
    };
    /** Gets the day of the week that the first of the month falls on. */
    CalendarFoundation.prototype._firstDayOfMonth = function () {
        var date = new Date(this._year, this._month, 1);
        return date.getDay();
    };
    /** Returns whether the passed in date is today. */
    CalendarFoundation.prototype._isToday = function (date) {
        var today = new Date();
        return this._isSameDate(date, today);
    };
    /** Determines if two nullable `Date` values are equal in that they are valid dates and represent the same day. */
    CalendarFoundation.prototype._isSameDate = function (first, second) {
        if (!first && !second) {
            return true;
        }
        else if (!first || !second) {
            return false;
        }
        else if (!isValidDate(first) || !isValidDate(second)) {
            return false;
        }
        return first.setHours(0, 0, 0, 0) === second.setHours(0, 0, 0, 0);
    };
    /** Returns whether the passed in date is in _selectedDates. */
    CalendarFoundation.prototype._isSelected = function (date) {
        var _this = this;
        var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, date); });
        if (index === -1) {
            return false;
        }
        return true;
    };
    /** Returns whether the given date is disabled */
    CalendarFoundation.prototype._isDisabled = function (date) {
        var _this = this;
        if (this._blockedDates.findIndex(function (d) { return _this._isSameDate(d, date); }) !== -1) {
            return true;
        }
        if (this._disabledDaysOfWeek && this._disabledDaysOfWeek.includes(date.getDay())) {
            return true;
        }
        if (typeof this._disableDayCallback === 'function' && this._disableDayCallback.call(null, date)) {
            return true;
        }
        if (!this._minDate && !this._maxDate) {
            return false;
        }
        if (this._minDate && date.getTime() < this._minDate.getTime()) {
            return true;
        }
        if (this._maxDate && date.getTime() > this._maxDate.getTime()) {
            return true;
        }
        return false;
    };
    /** Returns an array of events that fall on the given date */
    CalendarFoundation.prototype._getEventsForDate = function (date) {
        var _this = this;
        var eventsForDate = [];
        if (this._events) {
            eventsForDate = this._events.filter(function (event) {
                return _this._isSameDate(event.date, date);
            });
        }
        if (typeof this._eventsCallback === 'function') {
            var eventsFromCallback = this._eventsCallback(date);
            if (!eventsFromCallback) {
                return eventsForDate;
            }
            if (Array.isArray(eventsFromCallback)) {
                eventsForDate = eventsForDate.concat(eventsFromCallback);
            }
            else {
                eventsForDate.push(eventsFromCallback);
            }
        }
        return eventsForDate;
    };
    /** Calls before _onDateSelected */
    CalendarFoundation.prototype._beforeDateSelected = function (date) {
        var _this = this;
        if (this._dateSelectCallback) {
            Promise.resolve(this._dateSelectCallback(date)).then(function (res) {
                if (res) {
                    _this._onDateSelected(date.date);
                }
            });
        }
        else {
            this._onDateSelected(date.date);
        }
    };
    /** Add or remove a date from _selectedDates */
    CalendarFoundation.prototype._onDateSelected = function (date) {
        var _this = this;
        if (this.range) {
            return this._onDateRangeSelected(date);
        }
        var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, date); });
        var event = { date: date, isSelected: index > -1, type: CalendarDateSelectType.date };
        var isAllowed = this._adapter.emitHostEvent(CALENDAR_CONSTANTS.events.DATE_SELECTED, event, true, true);
        if (!isAllowed) {
            return;
        }
        if (!this._multiselect) {
            this._selectedDates = [];
        }
        if (index > -1) {
            if (this._multiselect) {
                this._selectedDates.splice(index, 1);
            }
        }
        else {
            this._selectedDates.push(date);
        }
        if (this._autoSwitch) {
            this._keepDateInView();
        }
        this._buildDates();
    };
    CalendarFoundation.prototype._onDateRangeSelected = function (date) {
        var _this = this;
        if (this._selectedLimit === this._selectedDates.length) {
            this._selectedDates = [];
            this._selectingRange = false;
        }
        if (this._selectedDates.length === 1 && date < this._selectedDates[0]) {
            this._selectedDates = [];
            this._selectingRange = false;
        }
        var dateRange = new DateRange({ from: this._selectedDates[0] || date, to: this._selectedDates.length === 1 ? date : this._selectedDates[1] });
        var alreadySelected = this._selectedDates.filter(function (d) { return _this._isSameDate(d, date); });
        var event = { range: dateRange, isSelected: alreadySelected.length > 1, type: CalendarDateSelectType.date };
        var selectedDatesLengthBeforeEvent = this._selectedDates.length;
        var isAllowed = this._adapter.emitHostEvent(CALENDAR_CONSTANTS.events.DATE_RANGE_SELECTED, event, true, true);
        // if the event was prevented or the state of the component was changed
        if (!isAllowed || selectedDatesLengthBeforeEvent !== this._selectedDates.length) {
            return;
        }
        if (alreadySelected.length > 1) {
            this._selectedDates.splice(this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, alreadySelected[0]); }));
        }
        else {
            if (this._selectedDates.length === 1 && this._rangeSelectionState === RangeSelectionState.from) {
                this._selectedDates = [date, this._selectedDates[0]];
                this._rangeSelectionState = RangeSelectionState.none;
            }
            else {
                this._selectedDates.push(date);
                this._rangeSelectionState = this._selectedDates.length === 2 ? RangeSelectionState.none : RangeSelectionState.to;
            }
        }
        if (this._autoSwitch) {
            this._keepDateInView();
        }
        this._viewIndex = CalendarView.Date;
        this._setActiveView();
        this._selectingRange = this._rangeSelectionState !== RangeSelectionState.none;
        this._buildDates();
    };
    /** Build an array of ICalendarMonths */
    CalendarFoundation.prototype._buildMonths = function () {
        var months = [];
        for (var i = 0; i < 12; i++) {
            var beforeMinDate = this._minDate ? this._year === this._minDate.getFullYear() && i < this._minDate.getMonth() : false;
            var afterMaxDate = this._maxDate ? this._year === this._maxDate.getFullYear() && i > this._maxDate.getMonth() : false;
            var month = {
                month: i,
                isActive: i === this._month && this._year === this._lastSelectedYear,
                disabled: beforeMinDate || afterMaxDate,
                onSelect: this._onMonthSelected.bind(this)
            };
            months.push(month);
        }
        this._adapter.setMonths(months);
    };
    /** Build an array of ICalendarYears */
    CalendarFoundation.prototype._buildYears = function () {
        var years = [];
        for (var i = this._maxYear; i >= this._minYear; i--) {
            var year = {
                year: i,
                isActive: i === this._year,
                onSelect: this._onYearSelected.bind(this)
            };
            years.push(year);
        }
        this._adapter.setYears(years);
    };
    CalendarFoundation.prototype._onPreviousButtonClick = function () {
        if (this._viewIndex === CalendarView.Month) {
            this._goToPreviousYear();
        }
        else {
            this._goToPreviousMonth();
        }
    };
    CalendarFoundation.prototype._onNextButtonClick = function () {
        if (this._viewIndex === CalendarView.Month) {
            this._goToNextYear();
        }
        else {
            this._goToNextMonth();
        }
    };
    /** Display the previous month */
    CalendarFoundation.prototype._goToPreviousMonth = function () {
        if (this._month === 0) {
            this.month = 11;
            this.year -= 1;
        }
        else {
            this.month -= 1;
        }
        this._buildDates();
    };
    /** Display the next month */
    CalendarFoundation.prototype._goToNextMonth = function () {
        if (this._month === 11) {
            this.month = 0;
            this.year += 1;
        }
        else {
            this.month += 1;
        }
        this._buildDates();
    };
    /** Display the previous year */
    CalendarFoundation.prototype._goToPreviousYear = function () {
        this.year--;
        this._buildMonths();
    };
    /** Display the next year */
    CalendarFoundation.prototype._goToNextYear = function () {
        this.year++;
        this._buildMonths();
    };
    /** Enter the month view */
    CalendarFoundation.prototype._onMonthViewEntered = function () {
        if (this._viewIndex === CalendarView.Date) {
            this._lastSelectedYear = this._year;
        }
        this._buildMonths();
        this._viewIndex = CalendarView.Month;
        this._setActiveView();
        var activeMonth = this._adapter.getActiveMonth();
        if (activeMonth !== null) {
            this._emitYearMonthChangeEvent(activeMonth);
        }
    };
    /** Enter the year view */
    CalendarFoundation.prototype._onYearViewEntered = function () {
        if (this._viewIndex === CalendarView.Date) {
            this._lastSelectedYear = this._year;
        }
        this._viewIndex = CalendarView.Year;
        this._adapter.updateYears(this._year);
        this._setActiveView();
        this._emitYearActiveChangeEvent();
    };
    /** Sets the active view */
    CalendarFoundation.prototype._setActiveView = function () {
        this._adapter.setActiveView(this._viewIndex);
        this._checkBoundaries();
    };
    /** Select a new month to display */
    CalendarFoundation.prototype._onMonthSelected = function (month) {
        var _this = this;
        this.month = month;
        var date = this._selectedDates[0];
        if (date) {
            // We need to check if the date value for the selected month is out of range and
            // reset to max date value for the new month if so
            this._clampDaysToMonth(date, month, date.getFullYear());
            // Now we can update the date with the new month
            date.setMonth(month);
        }
        // ignoring the range picker for now
        if (!this._range) {
            var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, date); });
            var event_1 = { date: date, isSelected: index > -1, type: CalendarDateSelectType.year };
            this._adapter.emitHostEvent(CALENDAR_CONSTANTS.events.DATE_SELECTED, event_1, true, true);
        }
        this._viewIndex = CalendarView.Date;
        this._buildDates();
        this._setActiveView();
        this._emitDateActiveChangeEvent();
    };
    /** Select a new year */
    CalendarFoundation.prototype._onYearSelected = function (year) {
        var _this = this;
        this.year = year;
        var date = this._selectedDates[0];
        if (date) {
            // We need to check if the date value for the selected month is out of range and
            // reset to max date value for the new month if so
            this._clampDaysToMonth(date, date.getMonth(), year);
            // Now we can update the date with the new year
            date.setFullYear(year);
        }
        // Ignoring the range picker for now
        if (!this._range) {
            var index = this._selectedDates.findIndex(function (d) { return _this._isSameDate(d, date); });
            var event_2 = { date: date, isSelected: index > -1, type: CalendarDateSelectType.year };
            this._adapter.emitHostEvent(CALENDAR_CONSTANTS.events.DATE_SELECTED, event_2, true, true);
        }
        this._viewIndex = CalendarView.Date;
        this._buildDates();
        this._setActiveView();
        this._emitDateActiveChangeEvent();
    };
    /** Ensures that the date value for the provided month is not higher than the max days in that month. */
    CalendarFoundation.prototype._clampDaysToMonth = function (date, month, year) {
        var day = date.getDate();
        var maxDaysInMonth = new Date(year, month + 1, 0).getDate();
        if (day > maxDaysInMonth) {
            date.setDate(maxDaysInMonth);
        }
    };
    CalendarFoundation.prototype._isMinMonth = function () {
        var isMinYear = this._year === this._minYear;
        var isMinMonth = !!this._minDate && this._month === this._minDate.getMonth();
        return isMinYear && isMinMonth;
    };
    CalendarFoundation.prototype._isMaxMonth = function () {
        var isMaxYear = this._year === this._maxYear;
        var isMaxMonth = !!this._maxDate && this._month === this._maxDate.getMonth();
        return isMaxYear && isMaxMonth;
    };
    /** Check and set whether the previous or next buttons should be disabled */
    CalendarFoundation.prototype._checkBoundaries = function () {
        if (this._viewIndex !== CalendarView.Year) {
            if (this._year === this._minYear && (this._viewIndex === CalendarView.Month || this._month === 0 || (this._minDate && this._month === this._minDate.getMonth()))) {
                this._adapter.disablePreviousButton();
            }
            else {
                this._adapter.enablePreviousButton();
            }
            if (this._year === this._maxYear && (this._viewIndex === CalendarView.Month || this._month === 11 || (this._maxDate && this._month === this._maxDate.getMonth()))) {
                this._adapter.disableNextButton();
            }
            else {
                this._adapter.enableNextButton();
            }
        }
    };
    /** Ensures that the most recently selected date is in view. */
    CalendarFoundation.prototype._keepDateInView = function () {
        var mostRecentDate = this._selectedDates && this._selectedDates.length ? this._selectedDates.reduce(function (a, b) { return (a > b ? a : b); }) : null;
        if (mostRecentDate) {
            this.month = mostRecentDate.getMonth();
            this.year = mostRecentDate.getFullYear();
        }
    };
    /** Creates a parse-able date string from a `Date` object. */
    CalendarFoundation.prototype._toSafeDateString = function (date) {
        return date.toLocaleDateString().replace(/[^ -~]/g, '');
    };
    Object.defineProperty(CalendarFoundation.prototype, "month", {
        /** Get and set the displayed month */
        get: function () {
            return this._month;
        },
        set: function (value) {
            if (this._month !== value && value >= 0 && value < 12) {
                this._month = value;
                if (this._isInitialized) {
                    this._adapter.setMonth(this._month);
                    this._checkBoundaries();
                    this._buildDates();
                }
                this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MONTH, this._month.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "year", {
        /** Get and set the displayed year */
        get: function () {
            return this._year;
        },
        set: function (value) {
            if (this._year !== value) {
                this._year = value;
                if (this._isInitialized) {
                    this._adapter.setYear(this._year);
                    this._checkBoundaries();
                    this._buildDates();
                }
                this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.YEAR, this._year.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "selectedDates", {
        /** Get and set the array of selected dates */
        get: function () {
            if (this.range) {
                return this._selectedRange.copy();
            }
            var dates = this._selectedDates.map(function (d) { return new Date(d.getTime()); });
            return this._multiselect ? dates : dates.length ? dates[0] : null;
        },
        set: function (value) {
            var dates = [];
            if (value) {
                if (this.range) {
                    this._selectedRange = new DateRange(value);
                    if (this._selectedRange.from) {
                        dates.push(this._selectedRange.from);
                    }
                    if (this._selectedRange.to) {
                        dates.push(this._selectedRange.to);
                    }
                    this._rangeSelectionState = this._selectedRange.from ? (this._selectedRange.to ? RangeSelectionState.none : RangeSelectionState.to) : RangeSelectionState.from;
                    this._selectingRange = this._rangeSelectionState === RangeSelectionState.to;
                }
                else {
                    if (this._multiselect) {
                        dates = Array.isArray(value) ? value : [value];
                    }
                    else {
                        if (Array.isArray(value)) {
                            dates = value.length ? [value[0]] : value;
                        }
                        else {
                            dates = [value];
                        }
                    }
                }
            }
            this._selectedDates = dates.map(function (d) { return new Date(d.getTime()); });
            if (this._isInitialized) {
                if (this._autoSwitch) {
                    this._keepDateInView();
                }
                this._buildDates();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "showToday", {
        /** Get and set whether today's date is highlighted. */
        get: function () {
            return this._showToday;
        },
        set: function (value) {
            if (this._showToday !== value) {
                this._showToday = value;
                if (value) {
                    this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.SHOW_TODAY);
                }
                else {
                    this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.SHOW_TODAY);
                }
                if (this._isInitialized) {
                    this._buildDates();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "multiselect", {
        /** Get and set whether multiple dates can be selected */
        get: function () {
            return this._multiselect;
        },
        set: function (value) {
            if (this._multiselect !== value) {
                if (!value && this._range) {
                    return;
                }
                this._multiselect = value;
                if (value) {
                    this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MULTISELECT);
                }
                else {
                    this._selectedDates = [this._selectedDates[this._selectedDates.length - 1]];
                    this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.MULTISELECT);
                    if (this._isInitialized) {
                        this._buildDates();
                    }
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "minDate", {
        /** Get and set the earliest selectable date */
        get: function () {
            if (!this._minDate) {
                return null;
            }
            return new Date(this._minDate.getTime());
        },
        set: function (value) {
            if (!this._isSameDate(this._minDate, value)) {
                if (isValidDate(value) && value !== null) {
                    this._minDate = new Date(value.getTime());
                    var newYear = this._minDate.getFullYear();
                    var newMonth = this._minDate.getMonth();
                    if (this._minYear !== newYear) {
                        this._minYear = newYear;
                        if (this._isInitialized) {
                            this._buildYears();
                        }
                    }
                    if (this._year < newYear) {
                        this._year = newYear;
                    }
                    if (this._year === newYear && this._month < newMonth) {
                        this.month = newMonth;
                    }
                    this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MIN_DATE, this._toSafeDateString(this._minDate));
                }
                else {
                    this._minDate = null;
                    this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.MIN_DATE);
                }
                if (this._isInitialized) {
                    this._checkBoundaries();
                    this._buildDates();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "maxDate", {
        /** Get and set the latest selectable date */
        get: function () {
            if (!this._maxDate) {
                return null;
            }
            return new Date(this._maxDate.getTime());
        },
        set: function (value) {
            if (!this._isSameDate(this._maxDate, value)) {
                if (isValidDate(value) && value !== null) {
                    this._maxDate = new Date(value.getTime());
                    var newYear = this._maxDate.getFullYear();
                    var newMonth = this._maxDate.getMonth();
                    if (this._maxYear !== newYear) {
                        this._maxYear = newYear;
                        if (this._isInitialized) {
                            this._buildYears();
                        }
                    }
                    if (this._year > newYear) {
                        this._year = newYear;
                    }
                    if (this._year === newYear && this._month > newMonth) {
                        this.month = newMonth;
                    }
                    this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MAX_DATE, this._toSafeDateString(this._maxDate));
                }
                else {
                    this._maxDate = null;
                    this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.MAX_DATE);
                }
                if (this._isInitialized) {
                    this._checkBoundaries();
                    this._buildDates();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "minYear", {
        /** Get and set the minimum selectable year */
        get: function () {
            return this._minYear;
        },
        set: function (value) {
            if (this._minYear !== value) {
                this._minYear = value;
                // Clamp the minYear if minDate is set
                if (this._minDate) {
                    var minDateYear = this._minDate.getFullYear();
                    if (this._minYear < minDateYear) {
                        this._minYear = minDateYear;
                    }
                }
                if (this._year < value) {
                    this.year = value;
                }
                this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MIN_YEAR, value.toString());
                if (this._isInitialized) {
                    this._buildYears();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "maxYear", {
        /** Get and set the maximum selectable year */
        get: function () {
            return this._maxYear;
        },
        set: function (value) {
            if (this._maxYear !== value) {
                this._maxYear = value;
                // Clamp the maxYear if maxDate is set
                if (this._maxDate) {
                    var maxDateYear = this._maxDate.getFullYear();
                    if (this._maxYear > maxDateYear) {
                        this._maxYear = maxDateYear;
                    }
                }
                if (this._year > value) {
                    this._year = value;
                }
                this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.MAX_YEAR, value.toString());
                if (this._isInitialized) {
                    this._buildYears();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "blockedDates", {
        /** Get and set the array of blocked dates */
        get: function () {
            var dates = this._blockedDates.map(function (d) { return new Date(d.getTime()); });
            return dates.length ? dates : null;
        },
        set: function (value) {
            var dates = [];
            if (value) {
                if (Array.isArray(value)) {
                    dates = value;
                }
                else {
                    dates = [value];
                }
            }
            this._blockedDates = dates.map(function (d) { return new Date(d.getTime()); });
            if (this._isInitialized) {
                this._buildDates();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "preventFocus", {
        get: function () {
            return this._preventFocus;
        },
        set: function (value) {
            if (this._preventFocus !== value) {
                this._preventFocus = value;
                if (this._preventFocus) {
                    this._adapter.setPreventFocusListener(this._preventFocusListener);
                }
                else {
                    this._adapter.removePreventFocusListener(this._preventFocusListener);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "autoSwitch", {
        get: function () {
            return this._autoSwitch;
        },
        set: function (value) {
            if (this._autoSwitch !== value) {
                this._autoSwitch = value;
                if (this._isInitialized && this._autoSwitch) {
                    this._keepDateInView();
                    this._buildDates();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "range", {
        get: function () {
            return this._range;
        },
        set: function (value) {
            if (this._range !== value) {
                this._range = value;
                this._applyRange();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "mode", {
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode !== value) {
                this._mode = value;
                this._applyMode();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "disabledDaysOfWeek", {
        get: function () {
            return this._disabledDaysOfWeek;
        },
        set: function (value) {
            if (value !== this._disabledDaysOfWeek) {
                this._disabledDaysOfWeek = value;
                this._applyDisabledDaysOfWeek();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "events", {
        //** Get and set the list of events */
        get: function () {
            return this._events;
        },
        set: function (value) {
            if (value !== this._events) {
                this._events = value;
                if (this._isInitialized) {
                    this._buildDates();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "showHeader", {
        /** Get and set whether the header is visible */
        get: function () {
            return this._showHeader;
        },
        set: function (value) {
            if (value !== this._showHeader) {
                this._showHeader = value;
                this._adapter.toggleHeaderVisibility(this._showHeader);
                if (this._showHeader) {
                    this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.SHOW_HEADER, this._showHeader.toString());
                    this._adapter.setMonth(this._month);
                    this._adapter.setYear(this._year);
                    this._checkBoundaries();
                    this._buildYears();
                }
                else {
                    this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.SHOW_HEADER);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarFoundation.prototype, "viewIndex", {
        //** Get and set the list of events */
        get: function () {
            return this._viewIndex;
        },
        set: function (value) {
            if (value !== this._viewIndex) {
                this._viewIndex = value;
                if (this._isInitialized) {
                    this._applyViewIndex();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    // Should only be used on initialization
    CalendarFoundation.prototype._applyViewIndex = function () {
        switch (this._viewIndex) {
            case CalendarView.Month:
                this._onMonthViewEntered();
                break;
            case CalendarView.Year:
                this._lastSelectedYear = this._year;
                this._onYearViewEntered();
                break;
            case CalendarView.Date:
                this._buildDates();
                this._setActiveView();
        }
    };
    Object.defineProperty(CalendarFoundation.prototype, "eventsCallback", {
        /** Set the callback to programmatically set events */
        set: function (value) {
            if (value !== this._eventsCallback) {
                this._eventsCallback = value;
                this._applyEventsCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    CalendarFoundation.prototype._applyEventsCallback = function () {
        if (this._isInitialized) {
            this._buildDates();
        }
    };
    Object.defineProperty(CalendarFoundation.prototype, "renderDateCallback", {
        /** Sets the date render callback */
        set: function (value) {
            if (value !== this._renderDateCallback) {
                this._renderDateCallback = value;
                this._applyRenderDateCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    CalendarFoundation.prototype._applyRenderDateCallback = function () {
        if (this._isInitialized) {
            this._buildDates();
        }
    };
    Object.defineProperty(CalendarFoundation.prototype, "disableDayCallback", {
        set: function (value) {
            if (value !== this._disableDayCallback) {
                this._disableDayCallback = value;
                this._applyDisableDayCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    CalendarFoundation.prototype._applyDisableDayCallback = function () {
        if (this._isInitialized) {
            this._buildDates();
        }
    };
    CalendarFoundation.prototype._applyDisabledDaysOfWeek = function () {
        if (this._isInitialized) {
            this._buildDates();
        }
    };
    Object.defineProperty(CalendarFoundation.prototype, "dateSelectCallback", {
        /** Function to call when a date is selected */
        set: function (callback) {
            this._dateSelectCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    CalendarFoundation.prototype._applyRange = function () {
        if (this._range) {
            this._adapter.setHostAttribute(CALENDAR_CONSTANTS.attributes.RANGE);
            this.multiselect = true;
            this._selectedLimit = 2;
            this._unregisterRangeListeners();
            this._registerRangeListeners();
        }
        else {
            this._adapter.removeHostAttribute(CALENDAR_CONSTANTS.attributes.RANGE);
            this.multiselect = false;
            this._selectedLimit = Infinity;
        }
    };
    CalendarFoundation.prototype._applyMode = function () {
        this._adapter.togglePickerDividerVisibility(this._mode === 'picker');
        // Rebuild the dates in case display mode was toggled
        this._buildDates();
    };
    CalendarFoundation.prototype._onCalendarHover = function (event) {
        var date = getEventPath(event).find(function (p) { return p.classList && p.classList.contains(CALENDAR_CONSTANTS.classes.DATE); });
        if (this.range) {
            this._handleRangeHover(event, date);
        }
    };
    CalendarFoundation.prototype._handleRangeHover = function (event, date) {
        if (this._selectingRange) {
            this._adapter.applyRangePreview(event, date, { from: this._selectedDates[0], to: this._selectedDates[1] });
        }
    };
    CalendarFoundation.prototype._onLeave = function (event) {
        this._handleRangeHover(event, undefined);
    };
    CalendarFoundation.prototype._registerRangeListeners = function () {
        this._adapter.registerCalenderHoverListener(this._calendarHoverListener);
        this._adapter.registerListenerOnDates('mouseleave', this._calendarMouseoutListener);
    };
    CalendarFoundation.prototype._unregisterRangeListeners = function () {
        this._adapter.unregisterCalenderHoverListener(this._calendarHoverListener);
        this._adapter.unregisterListenerOnDates('mouseleave', this._calendarMouseoutListener);
    };
    return CalendarFoundation;
}());
export { CalendarFoundation };
